/**
 * CRTEffect.ts — CSS-only CRT post-processing for xterm.js terminal.
 *
 * Applies scanlines, phosphor glow, and vignette via injected CSS.
 * Toggles via a `.crt-active` class on the container element.
 * Zero dependencies — pure CSS, no WebGL, no canvas, no rAF loop.
 *
 * Ported from RotaryKnights — phosphor glow changed from amber to icy blue
 * to match Gallowcrest's frozen theme.
 */

const CRT_STYLE_ID = 'crt-effect-styles';

const CRT_CSS = `
/* ── CRT Scanlines ───────────────────────────────────────────── */
.crt-active {
  position: relative;
}

.crt-active::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
  background: repeating-linear-gradient(
    to bottom,
    transparent 0px,
    transparent 2px,
    rgba(0, 0, 0, 0.25) 2px,
    rgba(0, 0, 0, 0.25) 3px
  );
}

/* ── CRT Vignette ────────────────────────────────────────────── */
.crt-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 11;
  background: radial-gradient(
    ellipse at center,
    transparent 60%,
    rgba(0, 0, 0, 0.20) 100%
  );
}

/* ── Phosphor Glow (icy blue for Gallowcrest) ─────────────────── */
.crt-active .xterm-screen {
  filter: brightness(1.1) contrast(1.05);
  text-shadow: 0 0 2px rgba(100, 180, 255, 0.15), 0 0 6px rgba(74, 158, 255, 0.08);
}
`;

export class CRTEffect {
  private container: HTMLElement;
  private enabled = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.injectStyles();
  }

  /** Inject the CRT stylesheet (once). */
  private injectStyles(): void {
    if (document.getElementById(CRT_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = CRT_STYLE_ID;
    style.textContent = CRT_CSS;
    document.head.appendChild(style);
  }

  /** Enable CRT effects. */
  enable(): void {
    if (this.enabled) return;
    this.container.classList.add('crt-active');
    this.enabled = true;
  }

  /** Disable CRT effects. */
  disable(): void {
    if (!this.enabled) return;
    this.container.classList.remove('crt-active');
    this.enabled = false;
  }

  /** Check if CRT effects are active. */
  isEnabled(): boolean {
    return this.enabled;
  }
}
